import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";

const LINKS = [
  { label: "Manifesto", href: "#manifesto", testid: "nav-manifesto-link" },
  { label: "Features", href: "#features", testid: "nav-features-link" },
  { label: "How It Works", href: "#how-it-works", testid: "nav-how-it-works-link" },
  { label: "Results", href: "#results", testid: "nav-testimonials-link" },
  { label: "Pricing", href: "#pricing", testid: "nav-pricing-link" },
  { label: "Community", href: "#community", testid: "nav-socials-link" },
];

const scrollTo = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 bg-obsidian/85 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => scrollTo("#top")}
          className="flex items-center gap-2 group"
        >
          <span className="grid place-items-center h-8 w-8 bg-volt text-white transition-transform duration-300 group-hover:rotate-12">
            <Zap className="h-4 w-4" strokeWidth={3} />
          </span>
          <span className="font-display font-black text-2xl tracking-tight uppercase">
            Fit<span className="text-volt">/</span>50
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => scrollTo(l.href)}
              className="font-mono text-xs tracking-[0.2em] uppercase text-white hover:text-volt transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <span className="hidden xl:flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-neutral-500">
            <span className="h-1.5 w-1.5 rounded-full bg-volt animate-pulse-dot" />
            Early Access Open
          </span>
          <button
            data-testid="nav-get-access-button"
            onClick={() => scrollTo("#access")}
            className="bg-volt text-white font-display font-bold uppercase tracking-wide px-5 py-2.5 text-sm hover:bg-volt-hover hover:shadow-[0_0_25px_rgba(255,107,0,0.5)] transition-[background-color,box-shadow] duration-200"
          >
            Get Early Access
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden text-neutral-200"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden border-t border-white/10 bg-obsidian/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`${l.testid}-mobile`}
                onClick={() => { setOpen(false); scrollTo(l.href); }}
                className="text-left font-display font-bold uppercase text-xl text-neutral-200 hover:text-volt transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-get-access-button-mobile"
              onClick={() => { setOpen(false); scrollTo("#access"); }}
              className="bg-volt text-white font-display font-bold uppercase px-5 py-3 mt-2"
            >
              Get Early Access
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
